'use client';
import * as React from 'react';
import { Fade, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Dashboard } from '@/shared/i18n';
import { Card, Skeleton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';

import CardWithModal from '../shared/card-with-modal';
import { ICourseData } from '../../types/course-data';
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import { PaginationType } from '../../types/pagination';
import { commonSwiperConfig } from '@/shared/utils';

interface ContentSectionBodyProps {
  data: any;
  isLoading: boolean;
  sliderBackgropColor?: string;
  title: string;
  isError: boolean;
  hasModal: boolean;
  dict: Dashboard;
  pagination?: PaginationType;
  type: 'slider' | 'grid';
  higlightItems?: boolean;
  hasNavigationButtons?: boolean;
  hasLink?: boolean;
  fetchMore?: (page: PaginationType['page'], pageSize: PaginationType['pageSize']) => void;
  onCardClickCallback?: (item: ICourseData) => void;
  lastCourseElementRef?: (node: HTMLElement | null) => void;
}

const ContentSectionBody: React.FC<ContentSectionBodyProps> = ({
  data,
  dict,
  isError,
  isLoading,
  hasModal,
  hasNavigationButtons = true,
  sliderBackgropColor,
  title,
  hasLink = false,
  pagination,
  type,
  fetchMore,
  higlightItems,
  onCardClickCallback,
  lastCourseElementRef,
}) => {
  const swiperRef = React.useRef<SwiperClass | null>(null);
  const isFetchingRef = React.useRef(false);

  // Reset fetching flag when pagination changes
  React.useEffect(() => {
    isFetchingRef.current = false;
  }, [pagination]);

  const [isBeginning, setIsBeginning] = React.useState(true);
  const [isEnd, setIsEnd] = React.useState(false);

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm')); // Mobile
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md')); // Tablet
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg')); // Small Desktop
  const isLg = useMediaQuery(theme.breakpoints.up('lg')); // Large Desktop

  const breakPoints: Record<
    number,
    {
      slidesPerView: number;
      centeredSlides: boolean;
      centeredSlidesBounds?: boolean;
    }
  > = {
    0: {
      slidesPerView: 1.2,
      centeredSlides: true,
      centeredSlidesBounds: true,
    },
    450: {
      slidesPerView: 1.2,
      centeredSlides: false,
    },
    600: {
      slidesPerView: 1.8,
      centeredSlides: false,
    },
    870: {
      slidesPerView: 2.2,
      centeredSlides: false,
    },
    1160: {
      slidesPerView: 3.2,
      centeredSlides: false,
    },
    1440: {
      slidesPerView: 4,
      centeredSlides: false,
    },
  };

  // Define how many skeleton cards to show based on screen size
  let skeletonCount = 4; // Default
  if (isXs)
    skeletonCount = 1; // Mobile
  else if (isSm)
    skeletonCount = 2; // Tablet
  else if (isMd)
    skeletonCount = 3; // Small Desktop
  else if (isLg) skeletonCount = 4; // Large Desktop
  if (isError)
    return (
      <div className="flex justify-center items-center py-10">
        C'è stato un problema nel recuperare i dati
      </div>
    );

  if (isLoading && !data?.length)
    return (
      <Grid container spacing={2} className="w-full">
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <Grid key={`skeleton-${i}`} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Card
              className="max-w-[318px] md:max-w-full p-4 bg-neutral-main/10 flex flex-col justify-start w-full h-full"
              sx={{ boxShadow: 'none' }}
            >
              <Skeleton
                sx={{ height: 190 }}
                animation="wave"
                variant="rectangular"
                className="rounded-xl"
              />
              <div className="pt-4 flex flex-col justify-between gap-y-4">
                <div>
                  <Skeleton height={25} className="rounded-lg" />
                  <Skeleton height={25} width="80%" className="rounded-lg" />
                </div>
                <div className="flex items-center gap-x-2">
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton height={25} width="100px" className="rounded-lg" />
                </div>
              </div>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  if (!isLoading && !data?.length)
    return (
      <section className="relative mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-x-2">
            <Typography variant="h3">{title}</Typography>
          </div>
        </div>
        <Fade in>
          <div className="h-[180px] md:h-[350px] w-full border border-[#414351] border-dotted rounded-xl flex flex-col justify-center items-center text-center relative">
            <img
              src="/assets/images/placeholder_no_item_dx.png"
              className="hidden md:block absolute h-[280px] md:right-[-16%] md:rotate-[-10deg] lg:rotate-[0deg] lg:right-[24px] xl:right-[45px] opacity-50"
            />
            <img
              src="/assets/images/placeholder_no_item_sx.png"
              className="hidden md:block absolute h-[280px] opacity-50 md:left-[-16%] md:rotate-[10deg] lg:rotate-[0deg] lg:left-[24px] xl:left-[45px]"
            />
            <div className="mb-6">
              <img
                src="/assets/images/search-content.svg"
                className="h-[40px] md:h-[80px] mx-auto"
              />
              <Typography variant="mac-sans-h4" component="p" className="opacity-30">
                Nessun contenuto qui… per ora.
              </Typography>
              <Typography variant="body-small-500" component="p" className="opacity-30">
                Esplora il catalogo e inizia a scrivere la tua playlist.
              </Typography>
            </div>
          </div>
        </Fade>
      </section>
    );
  return (
    <>
      <section className="relative mb-8">
        {/* Title and Action Buttons Row */}
        <div className="flex items-center justify-between mb-4">
          {/* Title */}
          <div className="flex items-center gap-x-2">
            <Typography variant="h3">{title}</Typography>
            {hasLink && (
              <IconButton>
                <ChevronRightRounded />
              </IconButton>
            )}
          </div>

          {/* Action Buttons */}
          <div className="hidden md:flex gap-2">
            <IconButton
              color="inherit"
              onClick={() => swiperRef.current?.slidePrev()}
              disabled={isBeginning}
            >
              <ChevronLeftRounded />
            </IconButton>
            <IconButton
              color="inherit"
              onClick={() => swiperRef.current?.slideNext()}
              disabled={isEnd}
            >
              <ChevronRightRounded />
            </IconButton>
          </div>
        </div>

        {/* Swiper Section */}
        <div className="relative overflow-hidden">
          <Swiper
            {...commonSwiperConfig}
            modules={[Navigation]}
            spaceBetween={16}
            breakpoints={breakPoints}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            onSlideChange={(swiper: SwiperClass) => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);

              // Prefetch logic
              if (fetchMore && pagination) {
                const currentIndex = swiper.activeIndex;
                const slidesPerView = breakPoints?.[swiper.currentBreakpoint]?.slidesPerView || 1;
                const totalSlides = data?.length || 0;

                // Calculate how many slides are left before the end
                const remainingSlides = totalSlides - (currentIndex + slidesPerView);

                // Prefetch threshold - fetch when 2 slides away from the end
                const prefetchThreshold = 2;

                if (
                  remainingSlides <= prefetchThreshold &&
                  pagination.page !== null &&
                  pagination.pageSize !== null &&
                  pagination.page < (pagination.pageCount || 1) &&
                  !isFetchingRef.current
                ) {
                  console.log('Fetching more data for carousel...');
                  isFetchingRef.current = true;
                  fetchMore(pagination.page + 1, pagination.pageSize);
                }
              }
            }}
          >
            {data?.map((item: ICourseData, idx: number) => (
              <SwiperSlide key={`course-slider-${idx}`}>
                <div className="course-card">
                  <CardWithModal
                    {...item}
                    hasModal={hasModal}
                    onCardClickCallback={onCardClickCallback}
                    highlighted={higlightItems || false}
                    isSlider
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Gradient Overlays */}
          {!isEnd && (
            <div
              className={`absolute top-0 right-0 z-20 h-full w-[100px] pointer-events-none bg-gradient-to-r from-transparent to-[${sliderBackgropColor || '#1C1D22'}]`}
            />
          )}
          {!isBeginning && (
            <div
              className={`absolute top-0 left-0 z-20 h-full w-[100px] pointer-events-none bg-gradient-to-l from-transparent to-[${sliderBackgropColor || '#1C1D22'}]`}
            />
          )}

          {data?.length > 0 && <div ref={lastCourseElementRef} />}
        </div>
      </section>
    </>
  );
};

export default ContentSectionBody;
